import React from "react";
import { Page } from "@/components/Layout";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, GraduationCap, Quotes, GearSix } from "@phosphor-icons/react";
import AboutValuesSection from "@/pages/about/AboutValuesSection";
import AboutPillarsSection from "@/pages/about/AboutPillarsSection";
import AboutPresenceSection from "@/pages/about/AboutPresenceSection";
import Seo from "@/components/Seo";

const FOUNDER_PHOTO = "/api/static/images/team-founder.jpg";
const FOUNDER_SIGNATURE = "/api/static/images/founder-signature.png";

const LEADERSHIP_PHOTOS = {
  shreeja: "/api/static/images/team-shreeja.jpg",
  shreeman: "/api/static/images/team-shreeman.jpg",
  advisor1: "/api/static/images/team-advisor-kunal.jpg",
};

const TIMELINE = [
  { year: "2024", label: "Founded", note: "MindDhara incorporated in Delhi NCR with the founding clinical & mentorship team." },
  { year: "2025", label: "Couch + Forward live", note: "Therapy directory, mentorship packages and the Free Consultation launched." },
  { year: "2026", label: "ATLAS publishing", note: "Research platform and the UK student-ambassador network go live." },
];

const LEADERSHIP = [
  {
    id: "shreeja-reddy",
    name: "Shreeja Reddy",
    role: "GMBPsS · Neuropsychology Practitioner, Researcher, Founder, Director",
    group: "Leadership",
    photo: LEADERSHIP_PHOTOS.shreeja,
    bio: "I didn't grow into this field through one title. I grew through many: Student → Registered Behaviour Technician → Therapy Assistant → Researcher → Assistant Psychologist → Neuropsychology Practitioner. At the core of all these identities is one constant: I am always a learner.",
  },
  {
    id: "shreeman-reddy",
    name: "Shreeman Reddy",
    role: "Co-Director · Computer Science Postgraduate",
    group: "Leadership",
    photo: LEADERSHIP_PHOTOS.shreeman,
    bio: "With a strong interest in how technology can advance human wellbeing. As the Co-Director of MindDhara Pvt Ltd, I'm helping shape the future of brain health through data-driven innovation, digital platforms, and interdisciplinary research.",
  },
  {
    id: "kunal-bahrani",
    name: "Dr. Kunal Bahrani",
    role: "MBBS, MD, DM (AIIMS), FEBN, MRCP (UK), FRCP (Glasgow), FRCP (London) · Director & Head of Neurology, Marengo Asia Hospitals, Faridabad.",
    group: "Advisory Board",
    photo: LEADERSHIP_PHOTOS.advisor1,
    bio: "Neurologist with almost two decades of expertise in stroke, epilepsy, dementia, Parkinson's disease, and headaches, leading one of North India's most respected neurology departments.",
  },
  {
    id: "alim-tunkara",
    name: "Mr. Alim Tunkara",
    role: "BSc Neuroscience, MSc Clinical Neuropsychiatry · Director & Clinical Support Lead, Neuspec Ltd, England, UK",
    group: "Advisory Board",
    photo: "/api/static/images/team-advisor-alim.jpg",
    bio: "With extensive expertise in acquired brain injury, complex neuropsychiatric recovery, and cognitive rehabilitation, he leads person-centred neurobehavioural services at Neuspec.",
  },
];

export default function About() {
  const [openBios, setOpenBios] = React.useState(() => new Set());
  const toggleBio = (id) => {
    setOpenBios((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  return (
    <Page>
      <Seo title="About Us · Brain, Behaviour and Culture" description="Meet the team behind MindDhara — therapy, mentorship and research that treats brain, behaviour and culture as one conversation." />
      {/* HERO BAND, dark, editorial */}
      <section className="md-section md-about-hero" data-testid="about-hero">
        <div className="md-about-hero-bg" aria-hidden />
        <div className="md-container md-about-hero-grid">
          <div>
            <div className="md-label-eyebrow md-about-hero-eyebrow">About <span className="md-brand">MindDhara</span></div>
            <h1 className="md-about-hero-title font-serif" data-testid="about-hero-title">
              Brain, behaviour and culture <span>treated as one conversation</span>
            </h1>
            <p className="md-about-hero-sub" data-testid="about-hero-sub">
              We help you understand your brain, care for your mind, and navigate life with greater clarity ,
              through therapy, mentorship and research that respects who you actually are.
            </p>
            <div className="md-about-hero-actions">
              <Link to="/couch?source=about-hero" className="btn-primary text-sm" data-testid="about-hero-couch">Explore Couch <ArrowRight size={14} weight="bold" /></Link>
              <Link to="/forward?source=about-hero" className="btn-outline text-sm" data-testid="about-hero-forward">Explore Forward <ArrowRight size={14} weight="bold" /></Link>
            </div>
          </div>
          <aside className="md-about-hero-stats" data-testid="about-hero-stats">
            <div><strong>120+</strong><span>families served</span></div>
            <div><strong>6+</strong><span>nationalities</span></div>
            <div><strong>12+</strong><span>languages</span></div>
            <div><strong>5.0★</strong><span>average rating</span></div>
          </aside>
        </div>
      </section>

      {/* OUR STORY, image left, narrative right (kept, slightly tightened) */}
      <section className="md-section md-about-story" data-testid="about-story-section">
        <div className="md-container">
          <div className="md-about-story-grid">
            <div className="md-about-story-photo" data-testid="about-story-photo">
              <img src={FOUNDER_PHOTO} alt="MindDhara founder Shreeja Reddy" loading="lazy" />
            </div>
            <div className="md-about-story-body">
              <div className="md-label-eyebrow mb-3">Our story</div>
              <h2 className="md-about-story-title font-serif" data-testid="about-story-title">
                A space built with <em>intention</em>
              </h2>
              <div className="md-about-story-prose">
                <p>
                  MindDhara began from recognising a cultural gap in how mental health is understood.
                  We were taught how to perform and adapt, but not how to understand the mind doing it all.
                </p>
                <p>
                  Struggles with attention, overwhelm, or identity were labelled as personal failings,
                  rather than reflections of deeper cognitive and cultural experiences.
                </p>
                <p>
                  MindDhara was created from this realisation: mental wellbeing cannot be separated from
                  cognition, and cognition cannot be separated from the worlds we are shaped by.
                </p>
                <p>
                  Here, brain, behaviour, and culture are not treated as separate conversations, but as one.
                </p>
                <blockquote className="md-about-story-quote">
                  <Quotes size={18} weight="fill" aria-hidden />
                  <span>
                    Many of us grew up hearing, <em>&ldquo;log kya kahengey&rdquo;</em> or <em>&ldquo;What will people think?&rdquo;</em>
                    <br />
                    We want to ask, <em>&ldquo;Parr aap kya kehna chaahtey ho&rdquo;</em> or <em>&ldquo;What do you want to express?&rdquo;</em>
                  </span>
                </blockquote>
                <p className="md-about-story-callout">
                  <em>Khud ko samajhne ka waqt aa gaya hai.</em>
                  <br />
                  <strong>It&apos;s time to meet yourself.</strong>
                </p>
              </div>
              <div className="md-about-story-sign">
                <img
                  src={FOUNDER_SIGNATURE}
                  alt="Shreeja Reddy signature"
                  className="md-about-story-sign-img"
                  data-testid="about-story-signature"
                />
                <div className="md-about-story-sign-name">Shreeja Reddy</div>
                <div className="md-about-story-sign-role">Founder, <span className="md-brand">MindDhara</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEADERSHIP & ADVISORY BOARD, card grid on dark green */}
      <section className="md-section md-about-leadership" data-testid="about-leadership-section">
        <div className="md-about-leadership-bg" aria-hidden />
        <div className="md-container">
          <div className="md-about-leadership-intro">
            <div className="md-label-eyebrow mb-3">The people behind it</div>
            <h2 className="text-3xl md:text-5xl font-serif" data-testid="about-leadership-title">
              Leadership <span style={{ color: "var(--md-primary)" }}>&amp;</span> Advisory Board
            </h2>
            <p className="text-base mt-3 md-about-leadership-sub">
              A small, deliberate team, clinicians, researchers and educators who shape how MindDhara cares,
              mentors and publishes.
            </p>
          </div>

          {["Leadership", "Advisory Board"].map((group) => (
            <div key={group} className="md-about-leadership-group" data-testid={`about-leadership-group-${group.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="md-label-eyebrow md-about-leadership-group-label">{group}</div>
              <div className="md-about-leadership-grid">
                {LEADERSHIP.filter((p) => p.group === group).map((p) => {
                  const isOpen = openBios.has(p.id);
                  return (
                    <article
                      key={p.id}
                      className="md-about-leadership-card"
                      data-testid={`about-leadership-card-${p.id}`}
                    >
                      <div className="md-about-leadership-portrait" aria-hidden>
                        {p.photo ? (
                          <img src={p.photo} alt={p.name} loading="lazy" />
                        ) : (
                          <GearSix size={96} weight="duotone" className="md-about-leadership-portrait-icon" />
                        )}
                      </div>
                      <div className="md-about-leadership-card-body">
                        <h3 className="md-about-leadership-card-name" data-testid={`about-leadership-name-${p.id}`}>
                          {p.name}
                        </h3>
                        <div className="md-about-leadership-card-role">{p.role}</div>
                        {isOpen && (
                          <p
                            id={`about-leadership-bio-${p.id}`}
                            className="md-about-leadership-card-bio"
                            data-testid={`about-leadership-bio-${p.id}`}
                          >
                            {p.bio}
                          </p>
                        )}
                        <button
                          type="button"
                          className="md-about-leadership-toggle"
                          onClick={() => toggleBio(p.id)}
                          aria-expanded={isOpen}
                          aria-controls={`about-leadership-bio-${p.id}`}
                          data-testid={`about-leadership-toggle-${p.id}`}
                        >
                          {isOpen ? "Hide bio" : "Read bio"}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* OUR VALUES, extracted */}
      <AboutValuesSection />

      {/* OUR WHY, WHAT & HOW, 3 dark cards on green */}
      <section className="md-section md-about-wwh" data-testid="about-wwh-section">
        <div className="md-about-wwh-bg" aria-hidden />
        <div className="md-container">
          <div className="md-about-wwh-intro">
            <h2 className="md-about-wwh-title font-serif" data-testid="about-wwh-title">
              Our <em style={{ color: "var(--md-primary)", fontStyle: "italic" }}>Why, What &amp; How</em>
            </h2>
          </div>
          <div className="md-about-wwh-grid">
            <article className="md-about-wwh-card" data-testid="about-wwh-why">
              <h3 className="md-about-wwh-card-title font-serif">Why we exist</h3>
              <p className="md-about-wwh-card-body">
                To redefine brain health and mind care through innovation grounded in South Asian identity, lived experience, and research, led by a team trained at leading institutions worldwide.
              </p>
            </article>
            <article className="md-about-wwh-card" data-testid="about-wwh-what">
              <h3 className="md-about-wwh-card-title font-serif">What we do</h3>
              <p className="md-about-wwh-card-body">
                We build inclusive systems that merge real-world lived experience with data, research, and clinical expertise to improve mental health and brain wellness.
              </p>
            </article>
            <article className="md-about-wwh-card" data-testid="about-wwh-how">
              <h3 className="md-about-wwh-card-title font-serif">How we do it</h3>
              <p className="md-about-wwh-card-body">
                By blending neuropsychology, psychiatry, and neuroscience, delivering care and education that are evidence-based, collaborative, and culturally informed.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* PILLARS, extracted */}
      <AboutPillarsSection />

      {/* ECOSYSTEM ENTRY, kept, slightly polished */}
      <section className="md-section md-section-tinted" data-testid="about-ecosystem-section">
        <div className="md-container">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="md-card md-card-hover" data-testid="about-couch-card">
              <Heart size={28} weight="duotone" style={{ color: "var(--md-primary)" }} />
              <h3 className="text-2xl font-serif mt-3">MindDhara Couch</h3>
              <p className="mt-2" style={{ color: "var(--md-text-soft)" }}>
                Mental health, cognitive health and everyday functioning, therapy, neuropsych
                support and structured pathways.
              </p>
              <Link to="/couch?source=about-pillars" className="btn-primary text-sm mt-6" data-testid="about-couch-cta">
                Explore Couch <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
            <div className="md-card md-card-hover" data-testid="about-forward-card">
              <GraduationCap size={28} weight="duotone" style={{ color: "var(--md-primary)" }} />
              <h3 className="text-2xl font-serif mt-3">MindDhara Forward</h3>
              <p className="mt-2" style={{ color: "var(--md-text-soft)" }}>
                Psychology mentorship and career guidance, for students, professionals and people
                in transition.
              </p>
              <Link to="/forward?source=about-pillars" className="btn-primary text-sm mt-6" data-testid="about-forward-cta">
                Explore Forward <ArrowRight size={14} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* SERVING WORLDWIDE, extracted */}
      <AboutPresenceSection />
    </Page>
  );
}
